/**
 * 音效管理工具
 * 提供各种提示音效的播放功能
 */

// 音效配置
const SOUND_CONFIG = {
  volume: 0.3, // 默认音量 (0-1)
  enabled: true // 默认开启
};

// 使用 Web Audio API 生成音效
class SoundGenerator {
  constructor() {
    this.audioContext = null;
    this.initAudioContext();
  }

  initAudioContext() {
    try {
      // 创建音频上下文
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContext();
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
    }
  }

  /**
   * 播放叮声 - 清脆的提示音
   * @param {number} frequency - 频率 (Hz)
   * @param {number} duration - 持续时间 (秒)
   * @param {number} volume - 音量 (0-1)
   */
  playDing(frequency = 800, duration = 0.1, volume = SOUND_CONFIG.volume) {
    if (!this.audioContext) return;

    try {
      // 恢复音频上下文（某些浏览器需要用户交互后才能播放）
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      const currentTime = this.audioContext.currentTime;

      // 创建振荡器（产生音调）
      const oscillator = this.audioContext.createOscillator();
      oscillator.type = 'sine'; // 正弦波，声音更柔和
      oscillator.frequency.setValueAtTime(frequency, currentTime);

      // 创建增益节点（控制音量）
      const gainNode = this.audioContext.createGain();
      gainNode.gain.setValueAtTime(volume, currentTime);
      
      // 添加淡出效果，让声音更自然
      gainNode.gain.exponentialRampToValueAtTime(0.01, currentTime + duration);

      // 连接节点
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // 播放
      oscillator.start(currentTime);
      oscillator.stop(currentTime + duration);
    } catch (error) {
      console.warn('Failed to play sound:', error);
    }
  }

  /**
   * 播放成功音效 - 上升的双音调
   */
  playSuccess(volume = SOUND_CONFIG.volume) {
    if (!this.audioContext) return;

    try {
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      const currentTime = this.audioContext.currentTime;

      // 第一个音符
      const osc1 = this.audioContext.createOscillator();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(523, currentTime); // C5

      const gain1 = this.audioContext.createGain();
      gain1.gain.setValueAtTime(volume, currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.01, currentTime + 0.1);

      osc1.connect(gain1);
      gain1.connect(this.audioContext.destination);
      osc1.start(currentTime);
      osc1.stop(currentTime + 0.1);

      // 第二个音符（稍高）
      const osc2 = this.audioContext.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(659, currentTime + 0.1); // E5

      const gain2 = this.audioContext.createGain();
      gain2.gain.setValueAtTime(volume, currentTime + 0.1);
      gain2.gain.exponentialRampToValueAtTime(0.01, currentTime + 0.2);

      osc2.connect(gain2);
      gain2.connect(this.audioContext.destination);
      osc2.start(currentTime + 0.1);
      osc2.stop(currentTime + 0.2);
    } catch (error) {
      console.warn('Failed to play success sound:', error);
    }
  }

  /**
   * 播放错误音效 - 下降的音调
   */
  playError(volume = SOUND_CONFIG.volume) {
    if (!this.audioContext) return;

    try {
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      const currentTime = this.audioContext.currentTime;

      const oscillator = this.audioContext.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(400, currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(200, currentTime + 0.15);

      const gainNode = this.audioContext.createGain();
      gainNode.gain.setValueAtTime(volume, currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, currentTime + 0.15);

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.start(currentTime);
      oscillator.stop(currentTime + 0.15);
    } catch (error) {
      console.warn('Failed to play error sound:', error);
    }
  }

  /**
   * 播放升级音效 - 上升的三音调
   */
  playLevelUp(volume = SOUND_CONFIG.volume) {
    if (!this.audioContext) return;

    try {
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      const currentTime = this.audioContext.currentTime;
      const notes = [523, 659, 784]; // C5, E5, G5

      notes.forEach((freq, index) => {
        const startTime = currentTime + index * 0.1;
        const osc = this.audioContext.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, startTime);

        const gain = this.audioContext.createGain();
        gain.gain.setValueAtTime(volume, startTime);
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.15);

        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        osc.start(startTime);
        osc.stop(startTime + 0.15);
      });
    } catch (error) {
      console.warn('Failed to play level up sound:', error);
    }
  }
}

// 创建全局音效生成器实例
const soundGenerator = new SoundGenerator();

/**
 * 音效播放器
 */
export const SoundEffects = {
  // 设置是否启用音效
  setEnabled(enabled) {
    SOUND_CONFIG.enabled = enabled;
  },

  // 获取音效启用状态
  isEnabled() {
    return SOUND_CONFIG.enabled;
  },

  // 设置音量
  setVolume(volume) {
    SOUND_CONFIG.volume = Math.max(0, Math.min(1, volume));
  },

  // 播放经验值获得音效
  playExp() {
    if (!SOUND_CONFIG.enabled) return;
    soundGenerator.playDing(800, 0.1, SOUND_CONFIG.volume);
  },

  // 播放连续学习音效
  playStreak() {
    if (!SOUND_CONFIG.enabled) return;
    soundGenerator.playDing(1000, 0.12, SOUND_CONFIG.volume);
  },

  // 播放成功音效
  playSuccess() {
    if (!SOUND_CONFIG.enabled) return;
    soundGenerator.playSuccess(SOUND_CONFIG.volume);
  },

  // 播放警告音效
  playWarning() {
    if (!SOUND_CONFIG.enabled) return;
    soundGenerator.playDing(600, 0.15, SOUND_CONFIG.volume);
  },

  // 播放错误音效
  playError() {
    if (!SOUND_CONFIG.enabled) return;
    soundGenerator.playError(SOUND_CONFIG.volume);
  },

  // 播放信息音效
  playInfo() {
    if (!SOUND_CONFIG.enabled) return;
    soundGenerator.playDing(700, 0.1, SOUND_CONFIG.volume);
  },

  // 播放升级音效
  playLevelUp() {
    if (!SOUND_CONFIG.enabled) return;
    soundGenerator.playLevelUp(SOUND_CONFIG.volume);
  },

  // 播放通用提示音
  playNotification() {
    if (!SOUND_CONFIG.enabled) return;
    soundGenerator.playDing(750, 0.1, SOUND_CONFIG.volume);
  }
};

export default SoundEffects;
